import { createTheme } from '@mui/material/styles';
import { Grow } from '@mui/material';

// ─────────────────────────────────────────────────────────────
//  PROSPO DESIGN SYSTEM — Single source of truth
//  All colors, typography, spacing, and component overrides live here.
//  Never hardcode hex values in components — always use theme tokens.
// ─────────────────────────────────────────────────────────────

// ── Border Radius Tokens ──────────────────────────────────────
// CHANGE THESE to update border radius across the entire app.
// - MUI components (Button, Card, Paper, Input etc.) use RADIUS_BASE via shape.borderRadius
// - Non-MUI elements (raw inputs, custom Box containers) import BORDER_RADIUS directly
export const RADIUS_BASE = 12;           // px number — used by MUI theme internally
export const BORDER_RADIUS = '12px';     // string — import this in component files
export const BORDER_RADIUS_SM = '8px';   // chips, badges, tooltips
export const BORDER_RADIUS_LG = '20px';  // dialogs / modals

export const prospoTheme = createTheme({
  // ── Palette ──────────────────────────────────────────────
  palette: {
    primary: {
      main: '#1976D2',
      light: '#E3F2FD',
      dark: '#1565C0',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#10B981',
      light: '#D1FAE5',
      dark: '#059669',
      contrastText: '#FFFFFF',
    },
    error: {
      main: '#EF4444',
      light: '#FEE2E2',
      dark: '#DC2626',
    },
    warning: {
      main: '#F59E0B',
      light: '#FEF3C7',
      dark: '#D97706',
    },
    info: {
      main: '#3B82F6',
      light: '#DBEAFE',
      dark: '#2563EB',
    },
    success: {
      main: '#10B981',
      light: '#D1FAE5',
      dark: '#059669',
    },
    background: {
      default: '#F8F9FC',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#111827',
      secondary: '#6B7280',
      disabled: '#D1D5DB',
    },
    divider: '#E5E7EB',
  },

  // ── Typography ────────────────────────────────────────────
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    fontWeightLight: 400,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 600,

    h1: { fontSize: '2rem',    fontWeight: 700, lineHeight: 1.2, color: '#111827' },
    h2: { fontSize: '1.5rem',  fontWeight: 700, lineHeight: 1.25, color: '#111827' },
    h3: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.3, color: '#111827' },
    h4: { fontSize: '1.125rem',fontWeight: 600, lineHeight: 1.35, color: '#111827' },
    h5: { fontSize: '1rem',    fontWeight: 600, lineHeight: 1.4, color: '#111827' },
    h6: { fontSize: '0.875rem',fontWeight: 600, lineHeight: 1.4, color: '#111827' },
    subtitle1: { fontSize: '1rem',    fontWeight: 500, lineHeight: 1.5, color: '#374151' },
    subtitle2: { fontSize: '0.875rem',fontWeight: 500, lineHeight: 1.5, color: '#374151' },
    body1: { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.6, color: '#374151' },
    body2: { fontSize: '0.8125rem', fontWeight: 400, lineHeight: 1.6, color: '#6B7280' },
    caption: { fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.5, color: '#6B7280' },
    overline: {
      fontSize: '0.6875rem',
      fontWeight: 500,
      lineHeight: 1.5,
      letterSpacing: '0.06em',
      textTransform: 'uppercase' as const,
      color: '#6B7280',
    },
    button: {
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: 1.5,
      textTransform: 'none' as const, // sentence case — never ALL CAPS
      letterSpacing: '0',
    },
  },

  // ── Shape ─────────────────────────────────────────────────
  shape: {
    borderRadius: RADIUS_BASE,
  },

  // ── Shadows ───────────────────────────────────────────────
  shadows: [
    'none',                                                                         // 0
    '0 1px 2px rgba(0,0,0,0.05)',                                                   // 1 — xs
    '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)',                     // 2 — sm
    '0 4px 6px rgba(0,0,0,0.07), 0 2px 4px rgba(0,0,0,0.04)',                     // 3 — md
    '0 10px 15px rgba(0,0,0,0.08), 0 4px 6px rgba(0,0,0,0.04)',                   // 4 — lg
    '0 20px 25px rgba(0,0,0,0.08), 0 10px 10px rgba(0,0,0,0.03)',                 // 5 — xl
    '0 25px 50px rgba(0,0,0,0.12)',                                                 // 6 — 2xl
    '0 1px 2px rgba(0,0,0,0.05)',
    '0 1px 3px rgba(0,0,0,0.08)',
    '0 4px 6px rgba(0,0,0,0.07)',
    '0 10px 15px rgba(0,0,0,0.08)',
    '0 20px 25px rgba(0,0,0,0.08)',
    '0 25px 50px rgba(0,0,0,0.12)',
    '0 1px 2px rgba(0,0,0,0.05)',
    '0 1px 3px rgba(0,0,0,0.08)',
    '0 4px 6px rgba(0,0,0,0.07)',
    '0 10px 15px rgba(0,0,0,0.08)',
    '0 20px 25px rgba(0,0,0,0.08)',
    '0 25px 50px rgba(0,0,0,0.12)',
    '0 1px 2px rgba(0,0,0,0.05)',
    '0 1px 3px rgba(0,0,0,0.08)',
    '0 4px 6px rgba(0,0,0,0.07)',
    '0 10px 15px rgba(0,0,0,0.08)',
    '0 20px 25px rgba(0,0,0,0.08)',
    '0 25px 50px rgba(0,0,0,0.12)',
  ],

  // ── Component Overrides ───────────────────────────────────
  components: {

    // Button
    MuiButton: {
      defaultProps: {
        disableElevation: true,
        variant: 'contained',
      },
      styleOverrides: {
        root: {
          borderRadius: RADIUS_BASE,
          padding: '9px 20px',
          fontWeight: 500,
          fontSize: '0.875rem',
          lineHeight: 1.5,
          textTransform: 'none',
          transition: 'all 0.15s ease',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 12px rgba(25,118,210,0.25)',
          },
          '&:active': {
            transform: 'translateY(0px)',
            boxShadow: 'none',
          },
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(25,118,210,0.25)',
          },
        },
        containedPrimary: {
          background: '#1976D2',
          '&:hover': {
            background: '#1565C0',
          },
        },
        outlined: {
          borderWidth: '1.5px',
          borderColor: '#E5E7EB',
          color: '#374151',
          '&:hover': {
            borderWidth: '1.5px',
            borderColor: '#D1D5DB',
            background: '#F9FAFB',
            boxShadow: 'none',
            transform: 'translateY(-1px)',
          },
        },
        text: {
          color: '#374151',
          '&:hover': {
            background: '#F9FAFB',
            boxShadow: 'none',
          },
        },
        sizeSmall: {
          padding: '6px 14px',
          fontSize: '0.8125rem',
        },
        sizeLarge: {
          padding: '12px 28px',
          fontSize: '0.9375rem',
        },
      },
    },

    // TextField / Input
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'small',
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: RADIUS_BASE,
          backgroundColor: '#FAFAFA',
          fontSize: '0.875rem',
          transition: 'border-color 0.15s, background-color 0.15s',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#E5E7EB',
            borderWidth: '1.5px',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#90CAF9',
          },
          '&.Mui-focused': {
            backgroundColor: '#FFFFFF',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#1976D2',
              borderWidth: '1.5px',
            },
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: '#EF4444',
          },
          '&.Mui-disabled': {
            backgroundColor: '#F3F4F6',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#E5E7EB',
            },
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: '#6B7280',
              color: '#6B7280',
            },
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          color: '#6B7280',
          '&.Mui-focused': {
            color: '#1976D2',
          },
          '&.Mui-error': {
            color: '#EF4444',
          },
          '&.Mui-disabled': {
            color: '#6B7280',
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: '0.75rem',
          marginTop: 4,
          marginLeft: 2,
        },
      },
    },

    // Select
    MuiSelect: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        root: {
          borderRadius: RADIUS_BASE,
          fontSize: '0.875rem',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          padding: '8px 16px',
          borderRadius: 6,
          margin: '1px 4px',
          '&:hover': {
            backgroundColor: '#E3F2FD',
          },
          '&.Mui-selected': {
            backgroundColor: '#E3F2FD',
            color: '#1976D2',
            fontWeight: 500,
            '&:hover': {
              backgroundColor: '#BBDEFB',
            },
          },
        },
      },
    },

    // Paper
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          borderRadius: RADIUS_BASE,
          border: '1px solid #E5E7EB',
          backgroundImage: 'none',
        },
        elevation1: {
          boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)',
          border: '1px solid #E5E7EB',
        },
        elevation2: {
          boxShadow: '0 4px 6px rgba(0,0,0,0.07), 0 2px 4px rgba(0,0,0,0.04)',
          border: '1px solid #E5E7EB',
        },
      },
    },

    // Card
    MuiCard: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          borderRadius: RADIUS_BASE,
          border: '1px solid #E5E7EB',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
          backgroundImage: 'none',
          transition: 'box-shadow 0.15s ease, transform 0.15s ease',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 24,
          '&:last-child': {
            paddingBottom: 24,
          },
        },
      },
    },

    // CssBaseline — global keyframes and utility classes
    MuiCssBaseline: {
      styleOverrides: `
        @keyframes rowFlash {
          0%   { background-color: #EEF2FF; }
          100% { background-color: transparent; }
        }
        .row-flash {
          animation: rowFlash 800ms ease forwards;
        }
      `,
    },

    // Dialog / Modal
    MuiDialog: {
      defaultProps: {
        TransitionComponent: Grow,
        transitionDuration: { enter: 200, exit: 160 },
      },
      styleOverrides: {
        paper: {
          borderRadius: 20,
          border: 'none',
          boxShadow: '0 25px 50px rgba(0,0,0,0.15), 0 10px 20px rgba(0,0,0,0.08)',
          padding: 0,
        },
        paperWidthSm: {
          maxWidth: 560,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: '28px 32px 16px 32px',
          fontSize: '1.125rem',
          fontWeight: 600,
          color: '#111827',
          lineHeight: 1.3,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '8px 32px 24px 32px',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '16px 32px 28px 32px',
          gap: 8,
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          // backgroundColor: 'rgba(17,24,39,0.18)',
          // backdropFilter: 'blur(0.5px)',
        },
      },
    },

    // Chip
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
          fontSize: '0.75rem',
          height: 26,
          '&.MuiChip-colorPrimary': {
            backgroundColor: '#E3F2FD',
            color: '#1976D2',
          },
          '&.MuiChip-colorSuccess': {
            backgroundColor: '#D1FAE5',
            color: '#059669',
          },
          '&.MuiChip-colorWarning': {
            backgroundColor: '#FEF3C7',
            color: '#D97706',
          },
          '&.MuiChip-colorError': {
            backgroundColor: '#FEE2E2',
            color: '#DC2626',
          },
          '&.MuiChip-colorInfo': {
            backgroundColor: '#DBEAFE',
            color: '#2563EB',
          },
          '&.MuiChip-colorDefault': {
            backgroundColor: '#F3F4F6',
            color: '#374151',
          },
        },
        label: {
          paddingLeft: 10,
          paddingRight: 10,
        },
      },
    },

    // Tabs
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 44,
          borderBottom: '1px solid #E5E7EB',
        },
        indicator: {
          height: 2,
          borderRadius: 2,
          backgroundColor: '#1976D2',
          transition: 'all 0.2s ease',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.875rem',
          color: '#6B7280',
          minHeight: 44,
          padding: '10px 16px',
          transition: 'color 0.15s ease',
          '&.Mui-selected': {
            color: '#1976D2',
            fontWeight: 600,
          },
          '&:hover': {
            color: '#374151',
          },
        },
      },
    },

    // Tooltip
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#111827',
          color: '#F9FAFB',
          fontSize: '0.75rem',
          fontWeight: 400,
          borderRadius: 8,
          padding: '6px 10px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
        },
        arrow: {
          color: '#111827',
        },
      },
    },

    // Alert
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: RADIUS_BASE,
          fontSize: '0.875rem',
          fontWeight: 400,
          alignItems: 'center',
          border: '1px solid transparent',
        },
        standardSuccess: {
          backgroundColor: '#D1FAE5',
          color: '#065F46',
          borderColor: '#A7F3D0',
          '& .MuiAlert-icon': { color: '#059669' },
        },
        standardError: {
          backgroundColor: '#FEE2E2',
          color: '#991B1B',
          borderColor: '#FECACA',
          '& .MuiAlert-icon': { color: '#DC2626' },
        },
        standardWarning: {
          backgroundColor: '#FEF3C7',
          color: '#92400E',
          borderColor: '#FDE68A',
          '& .MuiAlert-icon': { color: '#D97706' },
        },
        standardInfo: {
          backgroundColor: '#DBEAFE',
          color: '#1E40AF',
          borderColor: '#BFDBFE',
          '& .MuiAlert-icon': { color: '#2563EB' },
        },
      },
    },

    // Divider
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: '#E5E7EB',
        },
      },
    },

    // Table
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #F3F4F6',
          fontSize: '0.875rem',
          color: '#374151',
          padding: '14px 16px',
        },
        head: {
          fontSize: '0.75rem',
          fontWeight: 500,
          color: '#6B7280',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          backgroundColor: '#F9FAFB',
          borderBottom: '1px solid #E5E7EB',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'background-color 0.1s ease',
          '&:hover': {
            backgroundColor: '#F9FAFB',
          },
          '&:last-child td': {
            borderBottom: 'none',
          },
        },
      },
    },

    // Accordion
    MuiAccordion: {
      defaultProps: {
        disableGutters: true,
        elevation: 0,
      },
      styleOverrides: {
        root: {
          border: '1px solid #E5E7EB',
          borderRadius: '12px !important',
          overflow: 'hidden',
          '&:before': {
            display: 'none',
          },
          '&.Mui-expanded': {
            margin: 0,
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          padding: '0 20px',
          minHeight: 48,
          '&.Mui-expanded': {
            minHeight: 48,
          },
        },
        content: {
          margin: '12px 0',
          '&.Mui-expanded': {
            margin: '12px 0',
          },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: '0 20px 20px',
          borderTop: '1px solid #E5E7EB',
        },
      },
    },

    // LinearProgress
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          height: 6,
          backgroundColor: '#E3F2FD',
        },
        bar: {
          borderRadius: 8,
          backgroundColor: '#1976D2',
        },
      },
    },

    // CircularProgress
    MuiCircularProgress: {
      defaultProps: {
        color: 'primary',
      },
      styleOverrides: {
        root: {
          color: '#1976D2',
        },
      },
    },

    // Snackbar
    MuiSnackbar: {
      styleOverrides: {
        root: {
          '& .MuiPaper-root': {
            borderRadius: RADIUS_BASE,
          },
        },
      },
    },

    // Avatar
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          fontSize: '0.875rem',
          backgroundColor: '#E3F2FD',
          color: '#1976D2',
        },
      },
    },

    // Badge
    MuiBadge: {
      styleOverrides: {
        badge: {
          fontWeight: 600,
          fontSize: '0.625rem',
        },
      },
    },

    // List
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: RADIUS_BASE,
          padding: '8px 12px',
          margin: '1px 0',
          transition: 'all 0.15s ease',
          '&:hover': {
            backgroundColor: '#E3F2FD',
          },
          '&.Mui-selected': {
            backgroundColor: '#E3F2FD',
            color: '#1976D2',
            '&:hover': {
              backgroundColor: '#BBDEFB',
            },
          },
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontSize: '0.875rem',
          fontWeight: 500,
        },
        secondary: {
          fontSize: '0.75rem',
          color: '#6B7280',
        },
      },
    },

    // Drawer
    MuiDrawer: {
      styleOverrides: {
        paper: {
          border: 'none',
          boxShadow: '0 25px 50px rgba(0,0,0,0.12)',
        },
      },
    },

    // IconButton
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: RADIUS_BASE,
          transition: 'all 0.15s ease',
          '&:hover': {
            backgroundColor: '#F3F4F6',
          },
        },
        colorPrimary: {
          '&:hover': {
            backgroundColor: '#E3F2FD',
          },
        },
      },
    },

    // Switch
    MuiSwitch: {
      styleOverrides: {
        root: {
          width: 42,
          height: 26,
          padding: 0,
          '& .MuiSwitch-switchBase': {
            padding: 0,
            margin: 3,
            transitionDuration: '200ms',
            '&.Mui-checked': {
              transform: 'translateX(16px)',
              color: '#FFFFFF',
              '& + .MuiSwitch-track': {
                backgroundColor: '#1976D2',
                opacity: 1,
                border: 0,
              },
            },
          },
          '& .MuiSwitch-thumb': {
            width: 20,
            height: 20,
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
          },
          '& .MuiSwitch-track': {
            borderRadius: 13,
            backgroundColor: '#D1D5DB',
            opacity: 1,
          },
        },
      },
    },

    // Skeleton
    MuiSkeleton: {
      defaultProps: {
        animation: 'wave',
      },
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: '#F3F4F6',
          '&::after': {
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent)',
          },
        },
      },
    },

    // Menu / Popover
    MuiMenu: {
      styleOverrides: {
        paper: {
          border: 'none',
          borderRadius: RADIUS_BASE,
          boxShadow: '0 10px 15px rgba(0,0,0,0.08), 0 4px 6px rgba(0,0,0,0.04)',
          marginTop: 4,
        },
        list: {
          padding: '6px',
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          border: 'none',
          boxShadow: '0 10px 15px rgba(0,0,0,0.08), 0 4px 6px rgba(0,0,0,0.04)',
        },
      },
    },

    // Autocomplete
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          borderRadius: RADIUS_BASE,
          border: '1px solid #E5E7EB',
          boxShadow: '0 10px 15px rgba(0,0,0,0.08), 0 4px 6px rgba(0,0,0,0.04)',
          marginTop: 4,
        },
        option: {
          fontSize: '0.875rem',
          padding: '8px 12px',
          borderRadius: 6,
          margin: '1px 4px',
          '&[aria-selected="true"]': {
            backgroundColor: '#E3F2FD',
            color: '#1976D2',
          },
          '&:hover': {
            backgroundColor: '#F9FAFB',
          },
        },
        listbox: {
          padding: '6px',
        },
        tag: {
          margin: 2,
        },
      },
    },

    // Pagination
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
          fontSize: '0.875rem',
          '&.Mui-selected': {
            backgroundColor: '#1976D2',
            color: '#FFFFFF',
            '&:hover': {
              backgroundColor: '#1565C0',
            },
          },
        },
      },
    },

    // AppBar
    MuiAppBar: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          borderBottom: '1px solid #E5E7EB',
          color: '#111827',
        },
      },
    },

    // Breadcrumbs
    MuiBreadcrumbs: {
      styleOverrides: {
        ol: {
          flexWrap: 'nowrap',
        },
        li: {
          fontSize: '0.875rem',
          color: '#6B7280',
          '& a': {
            color: '#6B7280',
            textDecoration: 'none',
            '&:hover': {
              color: '#1976D2',
            },
          },
        },
        separator: {
          color: '#D1D5DB',
        },
      },
    },
  },
});

export default prospoTheme;
